import type { LoginResponse } from '@greatsumini/react-facebook-login';
import ReactFacebookLogin from '@greatsumini/react-facebook-login';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { ClientOnly } from 'remix-utils/client-only';
import type { FetcherData } from 'types';

import FacebookIcon from '~/assets/icons/Facebook';
import FacebookRoundIcon from '~/assets/icons/FacebookRound';
import SignUpMethodEvent from '~/events/SignUpMethodEvent';
import useEnv from '~/hooks/useEnv';
import { useRegistrationMethod } from '~/hooks/useRegistrationInteraction';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';
import { getPageViewId } from '~/utils/navigationContext';

import type { Props } from '.';
import type { SocialButtonAppearance } from './SocialButtonAppearance';
import {
  socialButtonClassNames,
  SocialButtonLabel
} from './SocialButtonAppearance';

export default function FacebookLoginButton({
  appearance = 'tile',
  onError,
  onSuccess,
  postAuthOptions,
  pagePosition
}: Props) {
  const { t } = useTranslation('common');
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const env = useEnv();
  const { isBot } = useRootData();
  const recordMethod = useRegistrationMethod();

  const errors = fetcher.data?.errors;

  function onFacebookLoginSuccess(res: LoginResponse['authResponse']) {
    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        productId: postAuthOptions?.productId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: res?.accessToken ?? '',
        viewId: getPageViewId() ?? ''
      },
      { action: '/bolt/login/facebook', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_FACEBOOK',
      pagePosition
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Meta);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess?.();
  }, [onSuccess, user.id]);

  useEffect(() => {
    if (errors?.message) onError(errors.message);
  }, [errors, onError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  const isLoggingIn = fetcher.state === 'submitting';

  function handleFacebookButtonClick() {
    new SignUpMethodEvent({
      buttonName: 'SIGN_IN_FACEBOOK',
      isBot,
      pagePosition: pagePosition || ''
    }).send();
    recordMethod('facebook');
  }

  const isTile = appearance === 'tile';

  return (
    <ClientOnly>
      {() => (
        <div
          className={
            isTile ? `${socialButtonClassNames.tile} text-black` : 'flex w-full'
          }
        >
          <ReactFacebookLogin
            appId={env.FACEBOOK_APP_ID}
            onSuccess={onFacebookLoginSuccess}
            fields="id,email"
            onFail={() =>
              onError(t('login.socialLoginError', { name: 'Facebook' }))
            }
            render={({ onClick }) => (
              <button
                className={
                  isTile
                    ? 'flex w-full items-center justify-center gap-2.5 text-base'
                    : socialButtonClassNames[appearance]
                }
                onClick={() => {
                  handleFacebookButtonClick();
                  if (onClick) onClick();
                }}
                style={{ pointerEvents: isLoggingIn ? 'none' : 'auto' }}
                type="button"
              >
                <FacebookLogo appearance={appearance} />
                <SocialButtonLabel
                  appearance={appearance}
                  isSubmitting={isLoggingIn}
                  name="Facebook"
                />
              </button>
            )}
          />
        </div>
      )}
    </ClientOnly>
  );
}

function FacebookLogo({ appearance }: { appearance: SocialButtonAppearance }) {
  if (appearance === 'tile') return <FacebookIcon height="24" width="24" />;

  if (appearance === 'link') {
    return <FacebookRoundIcon fill="#A9A9A9" height="16" width="16" />;
  }

  return <FacebookRoundIcon height="16" letterFill="white" width="16" />;
}
